import { catchError, EMPTY, merge, Observable, Subject, switchMap, tap } from 'rxjs';
import { inject, Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MyItems, MyItemsStore } from './my-items.store';
import { MenuBadge } from '../../models';
import { SessionRepository, PollingService, ProjectStore } from '@frontend-site/shared/core';
import { Router } from '@angular/router';
import { MyItemsServerStateEnum } from './my-items-state';
import { API_URL } from '@frontend-site/shared/data';
import { Destroyable, takeUntilDestroy } from '@frontend-site/shared/utils';
import { MENU_ITEM_REFRESHER_TOKEN } from '../main-item-refresher.provider';

const MY_ITEMS_POLLING_INTERVAL = 300000;

@Injectable()
export class MyItemsService extends Destroyable {
    private stopPolling$ = new Subject<void>();

    private refreshEvents = inject(MENU_ITEM_REFRESHER_TOKEN);

    constructor(
        private readonly http: HttpClient,
        private readonly store: MyItemsStore,
        private readonly projectStore: ProjectStore,
        private readonly sessions: SessionRepository,
        private router: Router,
        private pollingService: PollingService,
        @Inject(API_URL) private readonly apiUrl: string
    ) {
        super();
    }

    getMyItems(projectId: number, systemId: number | undefined): Observable<MyItems> {
        const params = new HttpParams().set('projectId', projectId);
        if (systemId) {
            params.set('systemId', systemId);
        }
        return this.http.get<MyItems>(`${this.apiUrl}/tmCommon/myMastheadCounts`, { params }).pipe(
            tap((res) => {
                this.store.setMyItems(res);
            })
        );
    }

    startMyItemsPolling(projectId: number, systemId?: number, intervalDuration = MY_ITEMS_POLLING_INTERVAL): Observable<MyItems> {
        this.callGetMyItemsUponExternalEvents(projectId, systemId);
        return this.pollingService
            .doPolling(this.getMyItems(projectId, systemId), {
                intervalInMS: intervalDuration,
                stopPolling: this.stopPolling$
            })
            .pipe(
                catchError((err) => {
                    console.error(err);
                    return EMPTY;
                })
            );
    }

    stopMyItemsPolling(): void {
        this.stopPolling$.next();
    }

    goToRelevantView(menuBadge: MenuBadge, projectId: number): void {
        switch (menuBadge.value) {
            case MyItemsServerStateEnum.MY_DEFECTS:
                this.router.navigate([`/tm/${projectId}/myDefects`]);
                break;
            case MyItemsServerStateEnum.MY_TESTS:
                this.router.navigate([`/tm/${projectId}/execution/myTests`]);
                break;
            case MyItemsServerStateEnum.MY_CORRECTIONS:
                this.router.navigate([`/${projectId}/corrections/my-corrections`]);
                break;
            case MyItemsServerStateEnum.MY_DEV_ISSUES:
                this.router.navigate([`/tm/${projectId}/myDevIssues`]);
                break;
        }
    }

    private callGetMyItemsUponExternalEvents(projectId: number, systemId: number | undefined): void {
        merge(...this.refreshEvents)
            .pipe(
                switchMap(() => {
                    return this.getMyItems(projectId, systemId);
                }),
                takeUntilDestroy(this)
            )
            .subscribe();
    }
}
